import type { ColumnDef } from '@tanstack/react-table'
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  Clock,
  Download,
  Eye,
  FileText,
  MoreHorizontal,
  XCircle,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import type {
  BillingHistory,
  BillingOverview,
} from '@/features/billing/types/billing.types'
import { EnhancedTable } from '@/shared/components/table/EnhancedTable'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Card, CardContent } from '@/shared/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { Skeleton } from '@/shared/components/ui/skeleton'

interface InvoicesTabProps {
  billingData: BillingOverview | null
}

export function InvoicesTab({ billingData }: InvoicesTabProps) {
  const [invoices, setInvoices] = useState<BillingHistory[]>([])
  const [_loading, _setLoading] = useState(false)

  useEffect(() => {
    if (billingData?.recent_invoices) {
      // In a real app, this would fetch all invoices, not just recent ones
      setInvoices(billingData.recent_invoices)
    }
  }, [billingData])

  const getStatusVariant = (status: BillingHistory['status']) => {
    switch (status) {
      case 'paid':
        return 'default'
      case 'pending':
        return 'secondary'
      case 'failed':
        return 'destructive'
      case 'cancelled':
        return 'outline'
      default:
        return 'outline'
    }
  }

  const getStatusIcon = (status: BillingHistory['status']) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className='w-4 h-4' />
      case 'pending':
        return <Clock className='w-4 h-4' />
      case 'failed':
        return <XCircle className='w-4 h-4' />
      case 'cancelled':
        return <AlertCircle className='w-4 h-4' />
      default:
        return <AlertCircle className='w-4 h-4' />
    }
  }

  const formatCurrency = (amount: number, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const handleDownloadInvoice = (invoice: BillingHistory) => {
    if (invoice.download_url) {
      window.open(invoice.download_url, '_blank')
    }
  }

  const handleViewInvoice = (_invoice: BillingHistory) => {
    // TODO: Navigate to invoice details
  }

  const _handleSendInvoice = (_invoice: BillingHistory) => {
    // TODO: Open email dialog
  }

  // Define table columns
  const invoiceColumns: ColumnDef<BillingHistory>[] = [
    {
      accessorKey: 'invoice_number',
      header: 'Invoice',
      size: 200,
      cell: ({ row }) => (
        <div>
          <div className='font-medium'>{row.original.invoice_number}</div>
          <div className='text-sm text-muted-foreground'>{row.original.id}</div>
        </div>
      ),
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      size: 150,
      cell: ({ row }) => (
        <div className='font-medium'>
          {formatCurrency(row.original.amount, row.original.currency)}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 120,
      cell: ({ row }) => {
        const isOverdue =
          row.original.status === 'pending' &&
          new Date(row.original.due_date) < new Date()
        return (
          <div className='flex flex-col gap-1'>
            <Badge
              variant={getStatusVariant(row.original.status)}
              className='gap-1 w-fit'
            >
              {getStatusIcon(row.original.status)}
              {row.original.status.charAt(0).toUpperCase() +
                row.original.status.slice(1)}
            </Badge>
            {isOverdue && (
              <Badge variant='destructive' className='text-xs w-fit'>
                Overdue
              </Badge>
            )}
          </div>
        )
      },
    },
    {
      accessorKey: 'issued_date',
      header: 'Issued',
      size: 130,
      cell: ({ row }) => (
        <div className='flex items-center gap-1'>
          <Calendar className='w-4 h-4 text-muted-foreground' />
          <span className='text-sm'>
            {formatDate(row.original.issued_date)}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'due_date',
      header: 'Due Date',
      size: 130,
      cell: ({ row }) => {
        const isOverdue =
          row.original.status === 'pending' &&
          new Date(row.original.due_date) < new Date()
        return (
          <div
            className={`flex items-center gap-1 ${isOverdue ? 'text-red-600' : ''}`}
          >
            <Calendar className='w-4 h-4 text-muted-foreground' />
            <span className='text-sm'>{formatDate(row.original.due_date)}</span>
          </div>
        )
      },
    },
    {
      accessorKey: 'period_start',
      header: 'Period',
      size: 200,
      cell: ({ row }) => (
        <div className='text-sm'>
          {formatDate(row.original.period_start)} -{' '}
          {formatDate(row.original.period_end)}
        </div>
      ),
    },
    {
      id: 'actions',
      header: '',
      size: 80,
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='ghost' size='sm' className='h-8 w-8 p-0'>
              <MoreHorizontal className='w-4 h-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end' className='w-48'>
            <DropdownMenuItem onClick={() => handleViewInvoice(row.original)}>
              <Eye className='w-4 h-4 mr-2' />
              View Details
            </DropdownMenuItem>
            {row.original.download_url && (
              <DropdownMenuItem
                onClick={() => handleDownloadInvoice(row.original)}
              >
                <Download className='w-4 h-4 mr-2' />
                Download PDF
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ]

  const _getTotalRevenue = () => {
    return invoices.reduce((sum, invoice) => {
      return invoice.status === 'paid' ? sum + invoice.amount : sum
    }, 0)
  }

  const _getOverdueInvoices = () => {
    const now = new Date()
    return invoices.filter(invoice => {
      return invoice.status === 'pending' && new Date(invoice.due_date) < now
    })
  }

  if (!billingData) {
    return (
      <div className='space-y-6'>
        {/* Invoices Table Skeleton */}
        <div className='bg-transparent border rounded-lg'>
          <div className='p-6 space-y-4'>
            {/* Table Header Skeleton */}
            <div className='grid grid-cols-7 gap-4 pb-4 border-b'>
              <Skeleton className='h-4 w-16' /> {/* Invoice */}
              <Skeleton className='h-4 w-16' /> {/* Amount */}
              <Skeleton className='h-4 w-14' /> {/* Status */}
              <Skeleton className='h-4 w-12' /> {/* Issued */}
              <Skeleton className='h-4 w-16' /> {/* Due Date */}
              <Skeleton className='h-4 w-14' /> {/* Period */}
              <Skeleton className='h-4 w-8' /> {/* Actions */}
            </div>

            {/* Table Rows Skeleton */}
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                key={i}
                className='grid grid-cols-7 gap-4 py-3 border-b border-border/30 last:border-0'
              >
                <div className='space-y-1'>
                  <Skeleton className='h-4 w-24' />
                  <Skeleton className='h-3 w-20' />
                </div>
                <Skeleton className='h-4 w-16' />
                <div className='space-y-1'>
                  <Skeleton className='h-5 w-16 rounded-full' />
                </div>
                <div className='flex items-center gap-1'>
                  <Skeleton className='h-4 w-4' />
                  <Skeleton className='h-3 w-16' />
                </div>
                <div className='flex items-center gap-1'>
                  <Skeleton className='h-4 w-4' />
                  <Skeleton className='h-3 w-16' />
                </div>
                <Skeleton className='h-3 w-24' />
                <Skeleton className='h-8 w-8' />
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='space-y-6'>
      {/* Invoices Table */}
      <div className='bg-transparent border'>
        {invoices.length > 0 ? (
          <div className='[&_table]:bg-transparent [&_thead]:bg-transparent [&_tr:hover]:bg-slate-50/50 dark:[&_tr:hover]:bg-slate-800/50 [&_tbody_tr]:cursor-pointer'>
            <EnhancedTable
              data={invoices}
              columns={invoiceColumns.map(col => ({
                ...col,
                enableSorting: false,
              }))}
              height='500px'
              className='bg-transparent'
              onRowClick={row => handleViewInvoice(row)}
            />
          </div>
        ) : (
          <Card>
            <CardContent>
              <div className='text-center py-12 text-muted-foreground'>
                <FileText className='w-12 h-12 mx-auto mb-4 opacity-50' />
                <h3 className='text-lg font-medium mb-2'>No invoices found</h3>
                <p className='text-sm max-w-sm mx-auto mb-4'>
                  You don't have any invoices yet. Invoices will appear here
                  once they are generated.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
